import React from 'react';
import styled from '@emotion/styled';
import { UseFormRegister, Control, FieldErrors, useFormContext } from 'react-hook-form';
import { MediaUploader } from '@edusense/shared-ui-media-upload';
import { Select as HeroSelect, SelectItem, Chip, type Selection } from '@heroui/react';
import type { BasicInfoFormData } from '@edusense/features-course-edit-core';
import type { BasicInfoTranslationSummary } from '../hooks/useCourseEditLogic';
import { COURSE_EDIT_TEST_IDS } from '@edusense/shared-constants';

// --- Styled Components ---
const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
`;

const StyledInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 1rem;
  &:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.5);
  }
  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

const StyledTextarea = styled.textarea`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  &:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.5);
  }
  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

// Select, FileInputWrapper 등 추가 스타일 컴포넌트
const FileInputWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FileInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-grow: 1;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.45rem 0.75rem;
  font-size: 0.875rem;
`;

const FileName = styled.span`
  color: #4b5563;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-grow: 1;
`;

const ThumbnailPreview = styled.img`
  margin-top: 0.75rem;
  max-width: 200px;
  max-height: 150px;
  border-radius: 0.375rem;
  border: 1px solid #e5e7eb;
`;


const ErrorMessage = styled.p`
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

// 커스텀 Input 컴포넌트 (간단한 버전)
const Input = styled.input<{ error?: string }>`
  padding: 0.75rem 1rem;
  border: 1px solid ${props => props.error ? '#ef4444' : '#d1d5db'};
  border-radius: 0.375rem;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;
  &:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.5);
  }
  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

const Textarea = styled.textarea<{ error?: string }>`
  padding: 0.75rem 1rem;
  border: 1px solid ${props => props.error ? '#ef4444' : '#d1d5db'};
  border-radius: 0.375rem;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  width: 100%;
  box-sizing: border-box;
  &:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.5);
  }
  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

// 간단한 Button 컴포넌트
const Button = styled.button<{ variant?: 'outline' | 'ghost', size?: 'sm' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.size === 'sm' ? '0.5rem 1rem' : '0.75rem 1.5rem'};
  font-size: ${props => props.size === 'sm' ? '0.875rem' : '1rem'};
  font-weight: 500;
  border-radius: 0.375rem;
  transition: all 0.2s;
  cursor: pointer;

  ${props => props.variant === 'outline' ? `
    background-color: white;
    border: 1px solid #d1d5db;
    color: #374151;
    &:hover {
      background-color: #f9fafb;
    }
  ` : props.variant === 'ghost' ? `
    background-color: transparent;
    border: none;
    color: #6b7280;
    &:hover {
      background-color: #f3f4f6;
    }
  ` : `
    background-color: #2563eb;
    border: 1px solid #2563eb;
    color: white;
    &:hover {
      background-color: #1d4ed8;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// DropdownMenu 컴포넌트들 (간단한 구현)
const LanguageControlRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
`;

const LanguageSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 180px;
`;

const LanguageFieldLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const LanguageStaticValue = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  color: #1f2937;
  font-weight: 500;
`;

const LanguageActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 180px;
`;

const HelperText = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  color: #6b7280;
`;

const LanguageChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const LanguageStateSnapshot = styled.span`
  display: none;
`;

const languageOptions = [
  { code: 'ko', label: '한국어' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
];

const LANGUAGE_LABELS: Record<string, string> = {
  ko: '한국어',
  en: 'English',
  'en-us': 'English (US)',
  'en-gb': 'English (UK)',
  ja: '日本語',
  zh: '中文(간체)',
  'zh-cn': '中文(간체)',
  'zh-tw': '中文(번체)',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
};

const getLanguageLabel = (languageCode: string): string => {
  const normalized = languageCode?.toLowerCase?.() ?? '';
  return LANGUAGE_LABELS[normalized] ?? (normalized ? normalized.toUpperCase() : languageCode);
};

export interface BasicInfoFormProps {
  register: UseFormRegister<BasicInfoFormData>;
  control: Control<BasicInfoFormData>;
  errors: FieldErrors<BasicInfoFormData>;

  // 썸네일 관련 props
  currentPreviewUrl: string;
  thumbnailFileName: string;
  thumbnailActions: {
    handleThumbnailChange: (event: React.ChangeEvent<HTMLInputElement>, setValue?: (name: string, value: any, options?: any) => void) => void;
    handleRemoveThumbnail: (setValue?: (name: string, value: any, options?: any) => void) => void;
    triggerThumbnailUpload: () => void;
  };
  thumbnailInputRef: React.RefObject<HTMLInputElement>;

  disabled?: boolean;
  saveLoading?: boolean;
  courseIdFromParent: string | undefined;
  currentLanguage: string;
  defaultLanguage: string;
  onLanguageChange: (languageCode: string) => void;
  onCopyFromDefault: () => void;
  translationSummary: BasicInfoTranslationSummary;
  onSupportedLanguagesChange?: (languages: string[]) => void;
  onDefaultLanguageChange?: (language: string) => void;
  supportedLanguagesOverride?: string[];
}

export const BasicInfoForm: React.FC<BasicInfoFormProps> = ({
  register,
  control: _control,
  errors,
  currentPreviewUrl,
  thumbnailFileName,
  thumbnailActions,
  thumbnailInputRef,
  disabled = false,
  saveLoading = false,
  currentLanguage,
  defaultLanguage,
  onLanguageChange,
  onCopyFromDefault,
  translationSummary,
  onSupportedLanguagesChange,
  onDefaultLanguageChange,
  supportedLanguagesOverride,
}) => {
  const { setValue, watch, register: contextRegister } = useFormContext<BasicInfoFormData>();
  const watchedSupportedLanguages = watch('supportedLanguages') ?? [];
  const supportedLanguages = React.useMemo(() => {
    const merged = new Set<string>();
    const push = (value: unknown) => {
      if (typeof value === 'string' && value.trim().length > 0) {
        merged.add(value);
      }
    };
    (Array.isArray(supportedLanguagesOverride) ? supportedLanguagesOverride : []).forEach(push);
    (Array.isArray(watchedSupportedLanguages) ? watchedSupportedLanguages : [watchedSupportedLanguages]).forEach(push);
    return Array.from(merged);
  }, [supportedLanguagesOverride, watchedSupportedLanguages]);
  const defaultLanguageValue = watch('defaultLanguage') ?? defaultLanguage;
  const normalizedDefaultLanguage = defaultLanguageValue?.toLowerCase?.() ?? 'ko';
  const selectableLanguages = supportedLanguages.length > 0 ? supportedLanguages : [normalizedDefaultLanguage];
  const canSwitchEditingLanguage = selectableLanguages.length > 1;
  const canChangeDefaultLanguage = selectableLanguages.length > 1;
  const effectiveCurrentLanguage = currentLanguage || selectableLanguages[0];
  const availableLanguageOptions = React.useMemo(() => {
    const optionMap = new Map(languageOptions.map(option => [option.code, option]));
    selectableLanguages.forEach(code => {
      if (!optionMap.has(code)) {
        optionMap.set(code, { code, label: getLanguageLabel(code) });
      }
    });
    return Array.from(optionMap.values());
  }, [selectableLanguages]);

  // 설정 탭 전용 토글(isPublic/allowComments)을 위해 폼 필드를 사전에 등록한다.
  React.useEffect(() => {
    contextRegister('supportedLanguages');
    contextRegister('defaultLanguage');
    contextRegister('isPublic');
    contextRegister('allowComments');
  }, [contextRegister]);

  const editingLanguageSelection = React.useMemo(() => new Set([effectiveCurrentLanguage]), [effectiveCurrentLanguage]);
  const supportedLanguageSelection = React.useMemo(() => new Set(selectableLanguages), [selectableLanguages]);
  const defaultLanguageSelection = React.useMemo(() => new Set([defaultLanguageValue]), [defaultLanguageValue]);

  const currentSummary = translationSummary[currentLanguage] ?? { isTitleMissing: false, isDescriptionMissing: false };
  const defaultLanguageLabel = getLanguageLabel(defaultLanguageValue);

  const renderLanguageOptionLabel = (languageCode: string): string => {
    const label = getLanguageLabel(languageCode);
    const summary = translationSummary[languageCode];
    const isMissing = summary?.isTitleMissing || summary?.isDescriptionMissing;
    if (languageCode === defaultLanguageValue) {
      return `${label} (기본)`;
    }
    return isMissing ? `${label} (미작성)` : label;
  };

  const ensureSelectionConsistency = (languages: string[], nextDefault?: string) => {
    if (languages.length === 0) {
      languages = [defaultLanguageValue];
    }

    const uniqueLanguages = Array.from(new Set(languages.filter(Boolean)));
    setValue('supportedLanguages', uniqueLanguages, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    onSupportedLanguagesChange?.(uniqueLanguages);

    const desiredDefault = nextDefault ?? defaultLanguageValue;
    if (!uniqueLanguages.includes(desiredDefault)) {
      const fallback = uniqueLanguages[0];
      setValue('defaultLanguage', fallback, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
      onDefaultLanguageChange?.(fallback);
    }

    if (!uniqueLanguages.includes(currentLanguage)) {
      const fallbackLanguage = uniqueLanguages[0];
      if (fallbackLanguage && fallbackLanguage !== currentLanguage) {
        onLanguageChange(fallbackLanguage);
      }
    }
  };

  const handleSupportedLanguagesChange = (keys: Selection) => {
    const nextSelected = keys === 'all'
      ? languageOptions.map(option => option.code)
      : Array.from(keys).map(key => key.toString());
    ensureSelectionConsistency(nextSelected);
  };

  const handleDefaultLanguageChange = (keys: Selection) => {
    const selectionArray = keys === 'all'
      ? supportedLanguages
      : Array.from(keys).map(key => key.toString());
    const selected = selectionArray[0];
    if (!selected) {
      return;
    }
    setValue('defaultLanguage', selected, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    onDefaultLanguageChange?.(selected);

    if (!supportedLanguages.includes(selected)) {
      ensureSelectionConsistency([...supportedLanguages, selected], selected);
    }
  };

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const api = {
      setSupportedLanguages: (languages: string[]) => {
        ensureSelectionConsistency(languages);
      },
      setDefaultLanguage: (language: string) => {
        const currentSet = supportedLanguages.length > 0 ? supportedLanguages : [normalizedDefaultLanguage];
        const next = Array.from(new Set([...currentSet, language]));
        setValue('defaultLanguage', language, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        onDefaultLanguageChange?.(language);
        ensureSelectionConsistency(next, language);
      },
      setCurrentLanguage: (language: string) => {
        if (language && language !== currentLanguage) {
          onLanguageChange(language);
        }
      },
      getState: () => ({
        supportedLanguages,
        defaultLanguage: defaultLanguageValue,
        currentLanguage,
      }),
    };

    const globalObj = window as typeof window & { __courseEditLanguageApi?: typeof api };
    globalObj.__courseEditLanguageApi = api;

    return () => {
      if (globalObj.__courseEditLanguageApi === api) {
        delete globalObj.__courseEditLanguageApi;
      }
    };
  }, [
    ensureSelectionConsistency,
    supportedLanguages,
    normalizedDefaultLanguage,
    defaultLanguageValue,
    currentLanguage,
    onLanguageChange,
    onDefaultLanguageChange,
  ]);

  return (
    <FormContainer onSubmit={(e) => e.preventDefault()}>
      <FieldGroup data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_SECTION}>
        <Label htmlFor="basic-info-language">언어</Label>
        <LanguageControlRow>
          <LanguageSelectWrapper data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_EDIT_SELECT}>
            <LanguageFieldLabel>편집 언어</LanguageFieldLabel>
            {canSwitchEditingLanguage ? (
              <HeroSelect
                aria-label="편집 언어 선택"
                selectionMode="single"
                selectedKeys={editingLanguageSelection}
                onSelectionChange={(keys) => {
                  const selections = keys === 'all'
                    ? selectableLanguages
                    : Array.from(keys).map(key => key.toString());
                  const selected = selections[0];
                  if (selected && selected !== currentLanguage) {
                    onLanguageChange(selected);
                  }
                }}
                isDisabled={disabled || saveLoading}
                placeholder="편집 언어 선택"
                data-testid={`${COURSE_EDIT_TEST_IDS.LANGUAGE_EDIT_SELECT}-trigger`}
              >
                {selectableLanguages.map(languageCode => (
                  <SelectItem
                    key={languageCode}
                    value={languageCode}
                    data-testid={`${COURSE_EDIT_TEST_IDS.LANGUAGE_EDIT_OPTION_PREFIX}-${languageCode}`}
                  >
                    {renderLanguageOptionLabel(languageCode)}
                  </SelectItem>
                ))}
              </HeroSelect>
            ) : (
              <LanguageStaticValue aria-live="polite">
                {renderLanguageOptionLabel(selectableLanguages[0])}
              </LanguageStaticValue>
            )}
          </LanguageSelectWrapper>
          <LanguageSelectWrapper data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_SUPPORTED_SELECT}>
            <LanguageFieldLabel>지원 언어</LanguageFieldLabel>
            <HeroSelect
              aria-label="지원 언어 선택"
              selectionMode="multiple"
              selectedKeys={supportedLanguageSelection}
              onSelectionChange={handleSupportedLanguagesChange}
              isDisabled={disabled || saveLoading}
              placeholder="지원 언어 선택"
              data-testid={`${COURSE_EDIT_TEST_IDS.LANGUAGE_SUPPORTED_SELECT}-trigger`}
            >
              {availableLanguageOptions.map(option => (
                <SelectItem
                  key={option.code}
                  value={option.code}
                  data-testid={`${COURSE_EDIT_TEST_IDS.LANGUAGE_SUPPORTED_OPTION_PREFIX}-${option.code}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </HeroSelect>
          </LanguageSelectWrapper>
          {canChangeDefaultLanguage && (
            <LanguageSelectWrapper data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_DEFAULT_SELECT}>
              <LanguageFieldLabel>기본 언어</LanguageFieldLabel>
              <HeroSelect
                aria-label="기본 언어 선택"
                selectionMode="single"
                selectedKeys={defaultLanguageSelection}
                onSelectionChange={handleDefaultLanguageChange}
                isDisabled={disabled || saveLoading}
                placeholder="기본 언어 선택"
                data-testid={`${COURSE_EDIT_TEST_IDS.LANGUAGE_DEFAULT_SELECT}-trigger`}
              >
                {selectableLanguages.map(languageCode => (
                  <SelectItem
                    key={languageCode}
                    value={languageCode}
                    data-testid={`${COURSE_EDIT_TEST_IDS.LANGUAGE_DEFAULT_OPTION_PREFIX}-${languageCode}`}
                  >
                    {renderLanguageOptionLabel(languageCode)}
                  </SelectItem>
                ))}
              </HeroSelect>
            </LanguageSelectWrapper>
          )}
          <LanguageActionWrapper data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_COPY_GROUP}>
            <LanguageFieldLabel>번역 도우미</LanguageFieldLabel>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCopyFromDefault}
              disabled={disabled || saveLoading || currentLanguage === defaultLanguage}
              data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_COPY_FROM_DEFAULT_BUTTON}
            >
              기본 언어 내용 복사
            </Button>
          </LanguageActionWrapper>
        </LanguageControlRow>
        <HelperText>
          기본 언어: {defaultLanguageLabel} · 현재 편집 언어: {getLanguageLabel(currentLanguage)}
          {currentSummary.isTitleMissing || currentSummary.isDescriptionMissing ? ' — 현재 언어의 번역이 완료되지 않았습니다.' : ''}
        </HelperText>
        <LanguageChipsRow data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_CHIPS}>
          {(supportedLanguages.length > 0 ? supportedLanguages : [normalizedDefaultLanguage]).map(languageCode => {
            const summary = translationSummary[languageCode] ?? { isTitleMissing: false, isDescriptionMissing: false };
            const chipColor = languageCode === defaultLanguageValue
              ? 'primary'
              : (summary.isTitleMissing || summary.isDescriptionMissing ? 'warning' : 'default');
            return (
              <Chip key={languageCode} color={chipColor} variant="flat" radius="sm">
                {renderLanguageOptionLabel(languageCode)}
              </Chip>
            );
          })}
        </LanguageChipsRow>
        <LanguageStateSnapshot data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_STATE_DEBUG} aria-hidden="true">
          {JSON.stringify({
            supported: supportedLanguages,
            defaultLanguage: defaultLanguageValue,
            currentLanguage,
          })}
        </LanguageStateSnapshot>
      </FieldGroup>

      <FieldGroup>
        <Label htmlFor="courseTitle">강의 제목 *</Label>
        <Input
          id="courseTitle"
          type="text"
          {...register('title', { required: "강의 제목은 필수입니다." })}
          disabled={disabled || saveLoading}
          placeholder="예: React로 만드는 실무형 웹 애플리케이션, Python 데이터 분석 완전정복"
          maxLength={100}
          error={errors.title?.message}
          aria-invalid={errors.title ? "true" : "false"}
          data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_TITLE_INPUT}
        />
        {errors.title && <ErrorMessage>{errors.title.message}</ErrorMessage>}
      </FieldGroup>

      <FieldGroup>
        <Label htmlFor="courseDescription">강의 설명</Label>
        <Textarea
          id="courseDescription"
          {...register('description')}
          rows={5}
          disabled={disabled || saveLoading}
          placeholder="이 강의에서 무엇을 배울 수 있는지 구체적으로 설명해주세요.
예: 이 강의는 React 18의 최신 기능을 활용하여 실무에서 바로 사용할 수 있는 웹 애플리케이션을 개발하는 방법을 배웁니다. Hook, Context API, 상태 관리, API 연동 등을 단계별로 학습하며, 최종적으로 완성된 프로젝트를 포트폴리오로 활용할 수 있습니다."
          error={errors.description?.message}
        />
        {errors.description && <ErrorMessage>{errors.description.message}</ErrorMessage>}
      </FieldGroup>

      <FieldGroup>
        <Label htmlFor="courseLevel">난이도 *</Label>
        <Input
          id="courseLevel"
          type="text"
          {...register('level', { required: "난이도는 필수입니다." })}
          disabled={disabled || saveLoading}
          placeholder="예: Python 기초, React 심화, DevOps 전문가"
          maxLength={50}
          error={errors.level?.message}
          aria-invalid={errors.level ? "true" : "false"}
          data-testid={COURSE_EDIT_TEST_IDS.LANGUAGE_LEVEL_INPUT}
        />
        {errors.level && <ErrorMessage>{errors.level.message}</ErrorMessage>}
      </FieldGroup>

      {/* 가격 설정은 별도 '가격' 탭에서 관리 */}

      {/* 썸네일 업로드 - MediaUploader 사용 */}
      <FieldGroup>
        <Label>썸네일 이미지</Label>
        <MediaUploader
          media={currentPreviewUrl ? [{
            id: 'thumbnail',
            url: currentPreviewUrl,
            type: 'image',
            fileName: thumbnailFileName || 'thumbnail.jpg'
          }] : []}
          uploadStatus="idle"
          uploadError={null}
          onUpload={thumbnailActions.triggerThumbnailUpload}
          onRemove={() => thumbnailActions.handleRemoveThumbnail(setValue)}
          maxItems={1}
        />
        <input
          type="file"
          id="thumbnail-upload-input-bif"
          ref={thumbnailInputRef}
          accept="image/*"
          onChange={(e) => thumbnailActions.handleThumbnailChange(e, setValue)}
          style={{ display: 'none' }}
          disabled={disabled || saveLoading}
        />
        {errors.thumbnailFile && <ErrorMessage>{errors.thumbnailFile.message}</ErrorMessage>}
      </FieldGroup>
    </FormContainer>
  );
};

export default BasicInfoForm;
